import { AvailableTimeslot } from 'entities/Form';
import { AvailableTimeslotV2 } from 'entities/FormV2';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { areDatesSame } from 'utils/helper';
import { getAvailableTimeslotDisplayLabelV2, getSiteAddressTranslationKeyV2 } from 'utils/helperV2';
import Tooltip from '../Tooltip';
import styles from './TimePicker.module.scss';

interface TimePickerPropsV2 {
  selectedDate?: Date;
  datePlaceholder?: string;
  selectedTime?: string;
  availableTimeslots: AvailableTimeslot[] | undefined;
  onSelect?: (fromDt: string, toDt: string) => void;
  onSelectSlot?: (timeSlot: AvailableTimeslot) => void;
  isSelected?: (timeSlot: AvailableTimeslot) => boolean;
  twoColumns?: boolean;
  hideTitle?: boolean;
}

const isAvailableTimeslotV2 = (timeSlot: AvailableTimeslot): timeSlot is AvailableTimeslotV2 =>
  'department' in timeSlot && 'serviceItemHid' in timeSlot;

const getSiteCodeClassName = (timeSlot: AvailableTimeslot) => {
  if (!isAvailableTimeslotV2(timeSlot)) return styles.siteCodeDefault;

  switch (timeSlot.department.siteCode?.trim().toUpperCase()) {
    case 'BH':
      return styles.siteCodeBH;
    case 'EKMC':
      return styles.siteCodeEKMC;
    default:
      return styles.siteCodeDefault;
  }
};

const TimePickerV2: FC<TimePickerPropsV2> = ({
  selectedDate,
  datePlaceholder,
  selectedTime,
  availableTimeslots,
  onSelect,
  onSelectSlot,
  isSelected,
  twoColumns,
  hideTitle = false,
}) => {
  const { t, i18n } = useTranslation();
  const sortedAvailableTimeslots = availableTimeslots
    ? [...availableTimeslots].sort((left, right) => left.fromDt.localeCompare(right.fromDt))
    : undefined;

  const getTimeLabel = (timeSlot: AvailableTimeslot) => {
    const label =
      i18n.language === 'zh-CN'
        ? timeSlot.labelSc
        : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
          ? timeSlot.labelTc
          : timeSlot.label;

    return isAvailableTimeslotV2(timeSlot) ? getAvailableTimeslotDisplayLabelV2(timeSlot, i18n.language) : label;
  };

  return (
    <div className={styles.container}>
      {!hideTitle && <div className={styles.title}>{t('availableTimeSlotsLabel')}</div>}
      {selectedDate ? (
        <div className={`${styles.times} ${twoColumns && styles.fourColumns}`}>
          {sortedAvailableTimeslots ? (
            sortedAvailableTimeslots.length > 0 ? (
              sortedAvailableTimeslots.map((time, index) => {
                const addressTranslationKey = isAvailableTimeslotV2(time)
                  ? getSiteAddressTranslationKeyV2(time.department.siteCode)
                  : undefined;
                const key = `${time.fromDt}-${time.toDt}-${
                  isAvailableTimeslotV2(time) ? `${time.department.hid}-${time.serviceItemHid}` : 'legacy'
                }-${index}`;

                return (
                  <Tooltip key={key} content={addressTranslationKey ? t(addressTranslationKey) : undefined}>
                    <button
                      className={`${styles.time} ${getSiteCodeClassName(time)} ${
                        (
                          isSelected
                            ? isSelected(time)
                            : selectedTime &&
                              selectedTime === time.fromDt &&
                              areDatesSame(selectedDate, new Date(time.fromDt))
                        )
                          ? styles.selected
                          : ''
                      }`}
                      onClick={() => (onSelectSlot ? onSelectSlot(time) : onSelect?.(time.fromDt, time.toDt))}
                    >
                      {getTimeLabel(time)}
                    </button>
                  </Tooltip>
                );
              })
            ) : (
              <span>{t('noTimeSlot')}</span>
            )
          ) : (
            <>
              <Skeleton height={32} borderRadius={'16px'} />
              <Skeleton height={32} borderRadius={'16px'} /> <Skeleton height={32} borderRadius={'16px'} />
              <Skeleton height={32} borderRadius={'16px'} /> <Skeleton height={32} borderRadius={'16px'} />
            </>
          )}
        </div>
      ) : (
        <span>{datePlaceholder ?? t('pleaseSelectDate')}</span>
      )}
    </div>
  );
};

export default TimePickerV2;
